/**
 * The structural document walk (R15/D76, extracted when D65's debt was paid)
 * — visit every object in a parsed pack with its JSON-ish path.
 *
 * @remarks
 * Two validation rules need to find EVERY instance of a shape wherever it
 * lives — `occurrence_tally_at_least` conditions (the producibility check)
 * and conditional-text nodes (the constant-condition check) — and both
 * refused to enumerate their host sites, because conditions and conditional
 * text live in many homes (trigger guards, voice-line gates, ask narratives,
 * lock refusals, log pages, `append_narrative` effects) and every
 * enumeration goes stale the day a new home is authored. Walking the parsed
 * document structurally and keying on the shape's own discriminant cannot go
 * stale, which is the whole argument for this module: the knowledge held
 * here is "how to visit a document", once, instead of "where condition-like
 * things live", twice and drifting.
 *
 * @packageDocumentation
 */

/**
 * Visit every object node in a document, depth-first, with its path.
 *
 * @param node - The document (or subtree) to walk
 * @param visit - Called for every object node with the node and its path (`''` for the root)
 * @param path - The path of `node` itself; callers omit it
 */
export const walkDocumentObjects = (
	node: unknown,
	visit: (record: Readonly<Record<string, unknown>>, path: string) => void,
	path = '',
): void => {
	if (Array.isArray(node)) {
		node.forEach((element, elementIndex) => {
			walkDocumentObjects(element, visit, `${path}[${String(elementIndex)}]`);
		});

		return;
	}

	if (typeof node !== 'object' || node === null) {
		return;
	}

	const record = node as Readonly<Record<string, unknown>>;

	visit(record, path);

	for (const [key, value] of Object.entries(record)) {
		walkDocumentObjects(value, visit, path === '' ? key : `${path}.${key}`);
	}
};

/**
 * One field off a value that may not be an object at all.
 *
 * @remarks
 * The walkers above hand their visitors `unknown` — a node's `variants` entry
 * is whatever the document put there — so every read of a field off one used
 * to need a `typeof … === 'object' && … !== null` guard first. On a PARSED
 * pack that guard can never be false, which made it a branch no test could
 * enter and no author could ever hit: dead weight that still had to be carried
 * past the coverage gate.
 *
 * Boxing removes the question instead of answering it. `Object(x)` returns an
 * object for every input — `{}` for `null` and `undefined`, a wrapper for a
 * primitive — so the read is total, a missing field is `undefined` either way,
 * and there is no branch left to be wrong about.
 *
 * @param value - Any node, of any shape
 * @param key - The field to read
 * @returns The field's value, or `undefined` when there is not one
 */
export const fieldOf = (value: unknown, key: string): unknown => (Object(value) as Readonly<Record<string, unknown>>)[key];
